/*!
 * Copyright © 2025-2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { environment } from '../../../environments/environment';

// The embedded camera-list page is mounted at integratedvideo/cameras/, one level below the video root.
export const VIDEO_ROOT_TARGET = '../';

/**
 * Returns true for root-relative same-origin paths (starts with "/" but not "//") and HTTPS
 * absolute URLs. Returns false for empty values and unsubstituted Octopus tokens such as
 * `#{VAR}`. Mirrors the coaching app's HostLinkResolver.isValidEmbeddedTarget.
 */
export function isValidEmbeddedTarget(value: string): boolean {
  if (!value) {
    return false;
  }
  if (value.startsWith('/') && !value.startsWith('//')) {
    return true;
  }
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Appends a MyAccount `?target=` deep link to an embedded page URL. The target is resolved by
 * MyAccount against the page's mount point and is encoded here because it may carry base64 ids
 * (`+` would otherwise be read back as a space).
 */
export function withEmbeddedTarget(embeddedPageUrl: string, target: string): string {
  const separator = embeddedPageUrl.includes('?') ? '&' : '?';
  return `${embeddedPageUrl}${separator}target=${encodeURIComponent(target)}`;
}

/**
 * Resolves the base URL for integrated video event links, applying the hub toggle.
 * When the hub is enabled and `videoListBaseUrl` is a valid embedded target, returns the embedded
 * URL ending in the video root, so appending a numeric event id yields a complete link. Otherwise falls
 * back to the legacy integratedvideo URL.
 */
export function resolveVideoListBaseUrl(hubEnabled: boolean, userLanguage: string): string {
  const videoListBaseUrl = environment.config.myAccountHost?.videoListBaseUrl;
  if (hubEnabled && isValidEmbeddedTarget(videoListBaseUrl)) {
    return withEmbeddedTarget(videoListBaseUrl, VIDEO_ROOT_TARGET);
  }
  return `${environment.config.legacyBaseUrl}/${userLanguage}/integratedvideo/`;
}

/**
 * Removes Angular router outlet parameters from a URL string.
 * @param outlet - The URL string that may contain outlet parameters
 * @returns The URL string with outlet parameters removed
 */
export function trimOutlet(outlet: string): string {
  if (!outlet) {
    return '';
  }
  // Remove outlet parameters like (param1:value1)
  return outlet.replace(/\([^)]*:[^)]*\)/g, '');
}

/**
 * Appends a path segment to a URL, optionally preserving query parameters.
 * Angular router outlet parameters are automatically removed from the URL.
 * @param currentUrl - The current URL to modify
 * @param newSegment - The new segment to append to the path
 * @param preserveQuery - Whether to preserve query parameters (default: true)
 * @returns The modified URL with the new segment appended and outlet parameters removed
 */
export function appendPathSegment(currentUrl: string, newSegment: string, preserveQuery: boolean = true): string {
  const cleanUrl = trimOutlet(currentUrl);
  const isAbsolute = cleanUrl.includes('://');
  const hasLeadingSlash = cleanUrl.startsWith('/');
  const url = new URL(cleanUrl, window.location.origin);
  url.pathname = url.pathname.replace(/\/+$/, '') + '/' + newSegment;

  if (!preserveQuery) {
    url.search = '';
  }

  if (isAbsolute) {
    return url.toString();
  } else {
    const result = url.pathname + url.search + url.hash;
    return hasLeadingSlash ? result : result.substring(1);
  }
}
